import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Chip, Collapse, Paper, Stack, Typography, type SxProps } from '@mui/material';
import React, { useState } from 'react';
import { statusLabel, toneFor, TONE_COLORS, type Tone } from './statusTones';

export function StatusChip({
  status,
  label,
  tone,
  large,
}: {
  status: string;
  label?: string;
  tone?: Tone;
  large?: boolean;
}) {
  const colors = TONE_COLORS[tone ?? toneFor(status)];
  return (
    <Chip
      component="span"
      size="small"
      label={label ?? statusLabel(status)}
      sx={{
        bgcolor: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        height: large ? 28 : 22,
        fontSize: large ? '0.85rem' : '0.72rem',
        fontWeight: large ? 600 : undefined,
      }}
    />
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  eyebrow?: React.ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      sx={{ mb: 3 }}
    >
      <Box sx={{ minWidth: 0, flex: 1, width: { xs: '100%', md: 'auto' } }}>
        {eyebrow && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{ mt: 0.5, maxWidth: 760 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  sx,
  noPadding,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps;
  noPadding?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', ...sx }}>
      {(title || action) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2.5, pt: 2, pb: noPadding ? 2 : 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && <Typography variant="h6">{title}</Typography>}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      <Box sx={{ p: noPadding ? 0 : 2.5 }}>{children}</Box>
    </Paper>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: Tone;
  icon?: React.ReactNode;
  onClick?: () => void;
}) {
  const colors = TONE_COLORS[tone];
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => onClick && (event.key === 'Enter' || event.key === ' ') && onClick()}
      sx={{
        p: 2,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 150ms',
        '&:hover': onClick ? { boxShadow: '0 6px 16px rgba(15,23,42,0.08)' } : undefined,
        borderLeft: tone === 'neutral' ? undefined : `4px solid ${colors.fg}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {icon && <Box sx={{ color: colors.fg, display: 'flex' }}>{icon}</Box>}
      </Stack>
      <Typography
        sx={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: '1.5rem', mt: 0.5 }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

export function KeyValueGrid({
  items,
  columns = 2,
}: {
  items: { label: string; value: React.ReactNode }[];
  columns?: number;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` },
        columnGap: 3,
        rowGap: 1.75,
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {item.label}
          </Typography>
          <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
            {item.value ?? '—'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function AboutThisPage({
  children,
  title = 'About this page',
}: {
  children: React.ReactNode;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Paper variant="outlined" sx={{ mb: 3, bgcolor: 'background.default' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          width: '100%',
          boxSizing: 'border-box',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <InfoOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {open ? 'Hide' : 'Show'}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box
          sx={{
            px: 2,
            pb: 2,
            typography: 'body2',
            color: 'text.secondary',
            '& p': { mt: 0, mb: 1 },
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
      <Typography variant="h6">{title}</Typography>
      {body && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: action ? 2 : 0 }}>
          {body}
        </Typography>
      )}
      {action}
    </Box>
  );
}

export function StatGrid({ children, min = 200 }: { children: React.ReactNode; min?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
        mb: 3,
      }}
    >
      {children}
    </Box>
  );
}

export function Money({
  value,
  precise,
  muted,
}: {
  value: number;
  precise?: boolean;
  muted?: boolean;
}) {
  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: precise ? 2 : 0,
    maximumFractionDigits: precise ? 2 : 0,
  });
  return (
    <Box
      component="span"
      sx={{ fontVariantNumeric: 'tabular-nums', color: muted ? 'text.secondary' : undefined }}
    >
      {formatter.format(value)}
    </Box>
  );
}
